import { useState, useEffect, useCallback } from 'react';
import { productionPacksAPI, finalAdSetsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Sparkles, Download, Film, ChevronLeft, ChevronRight,
  X, Loader2, Package, Eye, Unlink, ExternalLink,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';

const categoryBadge = {
  "Value Prop": "bg-blue-50 text-blue-700",
  "Price Comparison": "bg-amber-50 text-amber-700",
  "Process Oriented": "bg-violet-50 text-violet-700",
  "Testimonials": "bg-emerald-50 text-emerald-700",
  "UGC/Lifestyle": "bg-rose-50 text-rose-700",
  "Product Based": "bg-cyan-50 text-cyan-700",
};

export default function AdsFinalSets() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePack, setActivePack] = useState(null);   // pack currently open in slider
  const [sliderIndex, setSliderIndex] = useState(0);
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await finalAdSetsAPI.getAll();
      setItems(res.data || []);
    } catch {
      toast.error('Failed to load Final Ad Sets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUnlink = async (packId) => {
    try {
      await finalAdSetsAPI.remove(packId);
      toast.success('Removed from Final Ad Sets');
      setUnlinkTarget(null);
      if (activePack?.pack_id === packId) setActivePack(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const openSlider = (pack) => {
    setActivePack(pack);
    setSliderIndex(0);
  };

  const closeSlider = () => {
    setActivePack(null);
    setSliderIndex(0);
  };

  const downloadAll = (pack) => {
    const url = finalAdSetsAPI.getDownloadAllUrl(pack.pack_id);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${pack.title}_final_assets.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const downloadOne = (pack, media) => {
    const url = productionPacksAPI.getFinalAssetDownloadUrl(pack.pack_id, media.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = media.original_name || media.filename || 'asset';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ads-final-sets">
      {/* Header line */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
        <Sparkles size={16} className="text-amber-600 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Unified Ads Library</p>
          <p className="text-xs text-amber-800 mt-0.5">
            Every completed production pack you marked with <span className="font-semibold">"Use as Ad"</span> lives here. Browse, preview, and download the media individually or all at once.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center" data-testid="final-ad-sets-empty">
          <Sparkles className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="text-sm text-zinc-500 font-medium">No Final Ad Sets yet</p>
          <p className="text-xs text-zinc-400 mt-2">
            Go to Production &rarr; Completed and click <span className="font-medium">Use as Ad</span> on any pack to build your library.
          </p>
          <Button
            onClick={() => navigate('/production-packs')}
            className="mt-4 bg-zinc-900 text-white hover:bg-zinc-800 text-xs"
            data-testid="goto-production-btn"
          >
            <Package size={14} className="mr-1.5" /> Open Production Packs
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="final-ad-sets-grid">
          {items.map((pack) => {
            const count = (pack.final_assets_media || []).length;
            const firstMedia = (pack.final_assets_media || [])[0];
            const thumb = firstMedia ? productionPacksAPI.getFinalAssetUrl(pack.pack_id, firstMedia.id) : null;
            return (
              <div
                key={pack.pack_id}
                className="bg-white border border-zinc-200 rounded-lg overflow-hidden hover:shadow-md hover:border-zinc-300 transition-all group"
                data-testid={`final-ad-set-card-${pack.pack_id}`}
              >
                {/* Thumbnail with count badge */}
                <button
                  onClick={() => openSlider(pack)}
                  className="relative block w-full aspect-square bg-zinc-100 overflow-hidden"
                  data-testid={`open-slider-${pack.pack_id}`}
                >
                  {thumb ? (
                    firstMedia.is_video ? (
                      <video src={thumb} className="w-full h-full object-cover" muted />
                    ) : (
                      <img src={thumb} alt={pack.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-10 h-10 text-zinc-300" />
                    </div>
                  )}
                  {count > 1 && (
                    <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Film size={10} /> {count}
                    </span>
                  )}
                  <span className="absolute bottom-2 left-2 bg-white/95 text-zinc-900 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Eye size={12} /> View
                  </span>
                </button>

                {/* Body */}
                <div className="p-3 space-y-2">
                  <h3 className="text-sm font-medium text-zinc-900 line-clamp-2 leading-snug" title={pack.title}>
                    {pack.title}
                  </h3>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {pack.category && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${categoryBadge[pack.category] || 'bg-zinc-100 text-zinc-600'}`}>
                        {pack.category}
                      </span>
                    )}
                    {pack.post_type && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded-full">
                        {pack.post_type}
                      </span>
                    )}
                    {pack.posted && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">
                        Posted
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 pt-2 border-t border-zinc-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 h-7 text-xs border-zinc-200"
                      onClick={() => openSlider(pack)}
                      data-testid={`view-ad-set-${pack.pack_id}`}
                    >
                      <Eye size={12} className="mr-1" /> View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs border-zinc-200 text-zinc-700"
                      onClick={() => downloadAll(pack)}
                      title={count > 1 ? 'Download all as zip' : 'Download'}
                      data-testid={`download-all-${pack.pack_id}`}
                    >
                      <Download size={12} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs border-zinc-200 text-zinc-500 hover:text-red-600"
                      onClick={() => setUnlinkTarget(pack)}
                      title="Remove from Final Ad Sets"
                      data-testid={`unlink-${pack.pack_id}`}
                    >
                      <Unlink size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Slider Modal */}
      <Dialog open={!!activePack} onOpenChange={(o) => !o && closeSlider()}>
        <DialogContent
          className="max-w-5xl w-[95vw] bg-zinc-950 text-white border-zinc-800 p-0 overflow-hidden"
          data-testid="final-ad-set-slider"
        >
          {activePack && (() => {
            const media = activePack.final_assets_media || [];
            const current = media[sliderIndex];
            const total = media.length;
            const hasMany = total > 1;
            return (
              <div className="relative flex flex-col h-[90vh] max-h-[90vh]">
                <DialogTitle className="sr-only">{activePack.title} — Final Ad Set Preview</DialogTitle>
                <DialogDescription className="sr-only">
                  Browse and download the final assets attached to this production pack.
                </DialogDescription>
                {/* Top bar */}
                <div className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800 shrink-0">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold truncate" data-testid="slider-title">{activePack.title}</h3>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {hasMany ? `${sliderIndex + 1} of ${total}` : '1 asset'} · {activePack.category || 'Uncategorized'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 h-8 text-xs"
                      onClick={() => navigate(`/production-packs/${activePack.pack_id}`)}
                      data-testid="slider-open-pack"
                    >
                      <ExternalLink size={12} className="mr-1" /> Open Pack
                    </Button>
                    {current && (
                      <Button
                        size="sm"
                        className="bg-white text-zinc-900 hover:bg-zinc-200 h-8 text-xs"
                        onClick={() => downloadOne(activePack, current)}
                        data-testid="slider-download-current"
                      >
                        <Download size={12} className="mr-1" /> This
                      </Button>
                    )}
                    {hasMany && (
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-zinc-900 h-8 text-xs"
                        onClick={() => downloadAll(activePack)}
                        data-testid="slider-download-all"
                      >
                        <Download size={12} className="mr-1" /> All ({total})
                      </Button>
                    )}
                    <button
                      onClick={closeSlider}
                      className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white"
                      data-testid="slider-close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* Media viewport */}
                <div className="relative flex-1 flex items-center justify-center bg-black overflow-hidden">
                  {current ? (
                    current.is_video ? (
                      <video
                        key={current.id}
                        src={productionPacksAPI.getFinalAssetUrl(activePack.pack_id, current.id)}
                        controls
                        className="max-w-full max-h-full"
                        data-testid="slider-video"
                      />
                    ) : (
                      <img
                        key={current.id}
                        src={productionPacksAPI.getFinalAssetUrl(activePack.pack_id, current.id)}
                        alt={current.original_name}
                        className="max-w-full max-h-full object-contain"
                        data-testid="slider-image"
                      />
                    )
                  ) : (
                    <p className="text-zinc-500 text-sm">No media</p>
                  )}

                  {hasMany && (
                    <>
                      <button
                        onClick={() => setSliderIndex((i) => (i - 1 + total) % total)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                        data-testid="slider-prev"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        onClick={() => setSliderIndex((i) => (i + 1) % total)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
                        data-testid="slider-next"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                </div>

                {/* Thumbnail strip */}
                {hasMany && (
                  <div className="flex gap-2 px-4 py-3 bg-zinc-900 border-t border-zinc-800 overflow-x-auto shrink-0">
                    {media.map((m, idx) => {
                      const isActive = idx === sliderIndex;
                      const url = productionPacksAPI.getFinalAssetUrl(activePack.pack_id, m.id);
                      return (
                        <button
                          key={m.id}
                          onClick={() => setSliderIndex(idx)}
                          className={`relative w-16 h-16 shrink-0 rounded overflow-hidden border-2 transition-all ${
                            isActive ? 'border-amber-400' : 'border-transparent hover:border-zinc-600 opacity-60 hover:opacity-100'
                          }`}
                          data-testid={`slider-thumb-${idx}`}
                        >
                          {m.is_video ? (
                            <video src={url} className="w-full h-full object-cover" muted />
                          ) : (
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Unlink confirm */}
      <AlertDialog open={!!unlinkTarget} onOpenChange={(o) => !o && setUnlinkTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Final Ad Sets?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{unlinkTarget?.title}" from your Final Ad Sets library.
              The production pack and its media files remain untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleUnlink(unlinkTarget?.pack_id)}
              data-testid="confirm-unlink"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
